// ============================================
// 绑定应用 Hook
// 将 BindingEngine 的评估结果直接应用到 Three.js 材质
// ============================================

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDataBindingStore } from '../stores/dataBindingStore';
import { modelRefsRegistry } from '../stores/modelRefsRegistry';
import { useEmissiveArbiterStore } from '../stores/emissiveArbiter';
import type { BindingEvaluationResult } from '../types/databinding';

const DEBUG = false;
const log = (...args: any[]) => DEBUG && console.log('[BindingApply]', ...args);

// 保存原始材质颜色 (nodeId -> meshId -> originalColor)
const originalColors = new Map<string, Map<string, THREE.Color>>();

function saveOriginalColors(handle: { group: THREE.Group | null }, nodeId: string) {
  if (!handle.group) return;
  if (originalColors.has(nodeId)) return; // 已保存

  const meshColors = new Map<string, THREE.Color>();
  handle.group.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((mat, i) => {
        if (mat instanceof THREE.Material && 'color' in mat) {
          meshColors.set(`${child.uuid}:${i}`, (mat as any).color.clone());
        }
      });
    }
  });
  originalColors.set(nodeId, meshColors);
}

function restoreOriginalColors(handle: { group: THREE.Group | null }, nodeId: string) {
  if (!handle.group) return;
  const meshColors = originalColors.get(nodeId);
  if (!meshColors) return;

  handle.group.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((mat, i) => {
        if (mat instanceof THREE.Material && 'color' in mat) {
          const orig = meshColors.get(`${child.uuid}:${i}`);
          if (orig) {
            (mat as any).color.copy(orig);
            mat.needsUpdate = true;
          }
        }
      });
    }
  });
  originalColors.delete(nodeId);
}

/**
 * 直接修改 Three.js 材质属性
 */
function applyToThreeMaterial(
  handle: { group: THREE.Group | null },
  property: string,
  value: any,
  nodeId?: string
): boolean {
  if (!handle.group) return false;

  // 保存原始颜色（首次应用时）
  if (nodeId && (property === 'config.color' || property === 'material.color')) {
    saveOriginalColors(handle, nodeId);
  }

  let applied = false;

  handle.group.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const mats = Array.isArray(child.material) ? child.material : [child.material];

      for (const mat of mats) {
        if (!(mat instanceof THREE.Material)) continue;

        if (property === 'config.color' || property === 'material.color') {
          (mat as any).color?.setStyle(String(value));
          // 阶段3｜3.4：不再无条件清零 emissive——emissive 通道由仲裁器统一管理
          // （告警闪烁 > 选中高亮 > 悬停 > 数据变色），此处只改 color
          applied = true;
        }
        else if (property === 'config.metalness' || property === 'material.metalness') {
          if ('metalness' in mat) { (mat as any).metalness = Number(value); applied = true; }
        }
        else if (property === 'config.roughness' || property === 'material.roughness') {
          if ('roughness' in mat) { (mat as any).roughness = Number(value); applied = true; }
        }
        else if (property === 'config.opacity' || property === 'material.opacity') {
          if ('opacity' in mat) {
            (mat as any).opacity = Number(value);
            (mat as any).transparent = (mat as any).opacity < 1;
            applied = true;
          }
        }
        else if (property === 'config.emissive' || property === 'material.emissive') {
          // 阶段3｜3.4：登记到仲裁器（不直接写材质），由仲裁器按优先级决策
          if ('emissive' in mat && nodeId) {
            useEmissiveArbiterStore.getState().update(nodeId, { dataEmissive: String(value) });
          }
          applied = true;
        }
        else if (property === 'config.emissiveIntensity' || property === 'material.emissiveIntensity') {
          if ('emissiveIntensity' in mat && nodeId) {
            useEmissiveArbiterStore.getState().update(nodeId, { dataIntensity: Number(value) });
          }
          applied = true;
        }
        else if (property === 'config.wireframe' || property === 'material.wireframe') {
          if ('wireframe' in mat) { (mat as any).wireframe = !!value; applied = true; }
        }

        if (applied) mat.needsUpdate = true;
      }
    }
  });

  return applied;
}

/**
 * 将变换属性应用到 Three.js Group
 */
function applyToThreeTransform(
  handle: { group: THREE.Group | null },
  property: string,
  value: any
): boolean {
  if (!handle.group) return false;

  if (property === 'transform.position.x') { handle.group.position.x = Number(value); return true; }
  if (property === 'transform.position.y') { handle.group.position.y = Number(value); return true; }
  if (property === 'transform.position.z') { handle.group.position.z = Number(value); return true; }
  if (property === 'transform.rotation.x') { handle.group.rotation.x = Number(value) * (Math.PI / 180); return true; }
  if (property === 'transform.rotation.y') { handle.group.rotation.y = Number(value) * (Math.PI / 180); return true; }
  if (property === 'transform.rotation.z') { handle.group.rotation.z = Number(value) * (Math.PI / 180); return true; }
  if (property === 'transform.scale.x') { handle.group.scale.x = Number(value); return true; }
  if (property === 'transform.scale.y') { handle.group.scale.y = Number(value); return true; }
  if (property === 'transform.scale.z') { handle.group.scale.z = Number(value); return true; }

  return false;
}

/**
 * Hook: 监听绑定评估结果并直接应用到 Three.js 对象
 */
export function useBindingApply() {
  const appliedNodesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const dsManager = useDataBindingStore.getState().datasourceManager;

    const handleBindingApply = (results: BindingEvaluationResult[]) => {
      for (const result of results) {
        if (!result.success || !result.nodeId) continue;

        const handle = modelRefsRegistry.get(result.nodeId);
        if (!handle) continue;

        let applied = false;
        if (result.property.startsWith('transform.')) {
          applied = applyToThreeTransform(handle, result.property, result.value);
        } else {
          applied = applyToThreeMaterial(handle, result.property, result.value, result.nodeId);
        }

        if (applied) {
          appliedNodesRef.current.add(result.nodeId);
        }
      }
    };

    // 监听断开连接事件 - 恢复原始颜色
    const handleBindingReset = (nodeIds: string[]) => {
      for (const nodeId of nodeIds) {
        const handle = modelRefsRegistry.get(nodeId);
        if (handle) {
          restoreOriginalColors(handle, nodeId);
        }
      }
      appliedNodesRef.current.clear();
    };

    dsManager.on('binding:apply', handleBindingApply);
    dsManager.on('binding:reset', handleBindingReset);

    return () => {
      dsManager.off('binding:apply', handleBindingApply);
      dsManager.off('binding:reset', handleBindingReset);
    };
  }, []);
}
